import { users } from "@/api/users/useUsersQuery";
import { LeftActionBar, RightActionBar } from "@/features/layout";
import { useModal } from "@/features/modal/ModalStackManager";
import { WorkSlittingLeftActionButtonBar } from "@/features/outSourcingSlitting/actionBar/workSlittingLeftActionButtonBar";
import { WorkSlittingRightActionButtonBar } from "@/features/outSourcingSlitting/actionBar/workSlittingRightActionButtonBar";
import { WorkSlittingTableHead } from "@/features/outSourcingSlitting/workSlittingTableHead";
import {
  rowDataProps,
  WorkSlittingTableInRowBody,
} from "@/features/outSourcingSlitting/workSlittingTableInRowBody";
import { WorkSlittingTableInRowHead } from "@/features/outSourcingSlitting/workSlittingTableInRowHead";
import { WorkSlittingTableRow } from "@/features/outSourcingSlitting/workSlittingTableRow";
import { ChipsBox } from "@/features/standard/Chips/ChipsBox";
import { useQueryString } from "@/features/standardLayout/useQueryString";
import { Calendar, isCalendarDate } from "@/features/ui/Calendar";
import { DefectTypeItem, DefectTypeSet } from "@/features/ui/DefectTypeSet";
import { EquipmentAutoComplete } from "@/features/ui/autoComplete/equipment/equipment-autoComplete";
import { ItemAutoComplete } from "@/features/ui/autoComplete/item/item-autoComplete";
import { WorkActionBar } from "@/features/workByManagement/WorkActionBar";
import { WorkHeader } from "@/features/workByManagement/WorkHeader";
import { OperationOutsourceNotFound } from "@/features/workByManagement/WorkNotFound";
import { WorkProductionPlanTable } from "@/features/workByManagement/WorkProductionPlanTable";
import { WorkSiteSelect } from "@/features/workByManagement/WorkSiteSelect";
import { WorkTable } from "@/features/workByManagement/WorkTable";
import { WorkTableActionBar } from "@/features/workByManagement/WorkTableActionBar";
import { WorkTableBody } from "@/features/workByManagement/WorkTableBody";
import { WorkTableHead } from "@/features/workByManagement/WorkTableHead";
import { WorkTableRow } from "@/features/workByManagement/WorkTableRow";
import { WorkTableWrapper } from "@/features/workByManagement/WorkTableWrapper";
import { workPage } from "@/features/workByManagement/api/useWorkPageQuery";
import { useCheckboxContext } from "@/features/workByManagement/context/CheckboxProvider";
import useAutoResetState from "@/features/workByManagement/hooks/useAutoResetState";
import { useWorkActions } from "@/features/workByManagement/hooks/useWorkActions";
import { useWorkState } from "@/features/workByManagement/hooks/useWorkState";
import { useLoader } from "@/hooks/useLoader";
import useLocalStorage from "@/hooks/useLocalStorage";
import { useOutsourceWorkbyRowState } from "@/hooks/useReducerState";
import { getStartAndEndOfDateRange } from "@/utils/dateTimeUtil";
import { StatusObjType } from "@/utils/workStatus";
import styled from "@emotion/styled";
import { Button, Chip, Flex, Menu, Pagination, Select } from "@mantine/core";
import {
  AuthSignupPost201ResponseEquipmentWorksInner,
  ProductionPlansGet200ResponseRowsInnerWorksInner,
  ProductionPlansGet200ResponseRowsInnerWorksInnerTrackingStatusEnum,
  WorksItemGet200ResponseRowsInner,
  WorksSlittingScrapDetailPost200ResponseInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar, IconSettings } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const Work = {
  ActionBar: WorkActionBar,
  LeftActionBar: LeftActionBar,
  RightActionBar: RightActionBar,
  SiteSelect: WorkSiteSelect,
  Select: Select,
  Button: Button,
  WorkHeader: WorkHeader,
  WorkTableActionBar: WorkTableActionBar,
  WorkTableWrapper: WorkTableWrapper,
  WorkProductionPlanTable: WorkProductionPlanTable,
  WorkTable: WorkTable,
  WorkTableHead: WorkTableHead,
  WorkTableBody: WorkTableBody,
  WorkTableRow: WorkTableRow,
  WorkSlittingTableHead: WorkSlittingTableHead,
  WorkSlittingTableRow: WorkSlittingTableRow,
  WorkSlittingTableInRowHead: WorkSlittingTableInRowHead,
  WorkSlittingTableInRowBody: WorkSlittingTableInRowBody,
};

export const trackingStatusObj: StatusObjType = {
  WAITING: "대기",
  PAUSED: "일시정지",
  WORKING: "진행중",
  DONE: "완료",
};

export const ProductionOutsourcingSlittingPage = () => {
  const state = useWorkState();
  const actions = useWorkActions();
  const { resetAllChecked, setCheckedState, setWorkData } = useCheckboxContext();

  const [activePage, setPage] = useState(1);
  const [equipmentCode, setEquipmentCode] = useState<string | null>(null);
  const [itemCode, setItemCode] = useState<string | null>(null);
  const [matchedData, setMatchedData] = useState<
    Record<number, WorksSlittingScrapDetailPost200ResponseInner>
  >({}); // matchedData를 상태로 관리
  const [partChecked, setPartChecked] = useState<Record<number, boolean>>({});
  const [startDate, setStartDate] = useQueryString<Date | null>("startDate", state.date[0]);
  const [endDate, setEndDate] = useQueryString<Date | null>("endDate", state.date[1]);
  const { state: workRowState, toggle } = useOutsourceWorkbyRowState();
  const { openModal } = useModal();
  const [defectTypes, setDefectTypes] = useLocalStorage<DefectTypeItem[][]>("defectTypes", [
    [],
    [],
  ]);
  // useAutoResetState를 사용하여 상태를 관리
  const [updateSuccess, setUpdateSuccess] = useAutoResetState<boolean>(false, 50);

  const { setLoading, LoadingOverlay } = useLoader();
  const { t } = useTranslation();

  const { data: whoData } = useQuery(users.who({}));

  // OSPPONo, PODate, ProcName, CustName, ItemName,

  const { data, refetch } = useQuery(
    workPage.workItemForOutSource({
      defectCodes: (defectTypes && defectTypes[0]) ?? (defectTypes && defectTypes[1]),
      query: {
        $and: [
          {
            // 최근 작업일 기준으로 변경 (2024-07-31 jswon)
            lastWorkDate: {
              $between: getStartAndEndOfDateRange(startDate, endDate),
            },
            // scheduledAt: {
            //     $between: getStartAndEndOfDateRange(state.date[0], state.date[1])
            // }
          },
          {
            trackingStatus: Object.keys(workRowState).reduce(
              (
                result: ProductionPlansGet200ResponseRowsInnerWorksInnerTrackingStatusEnum[],
                key: string,
              ) => {
                if (workRowState[key as keyof typeof workRowState]) {
                  result.push(
                    key as ProductionPlansGet200ResponseRowsInnerWorksInnerTrackingStatusEnum,
                  );
                }
                return result;
              },
              [],
            ),
          },
          {
            routingOutsourceId: { $ne: null },
          },
          ...(equipmentCode
            ? [
                {
                  equipmentCode: {
                    $eq: equipmentCode,
                  },
                },
              ]
            : []),
          ...(itemCode
            ? [
                {
                  itemCode: {
                    $eq: itemCode,
                  },
                },
              ]
            : []),
          ...(whoData?.data?.partner?.code
            ? [
                {
                  partnerCode: {
                    $eq: whoData?.data?.partner?.code,
                  },
                },
              ]
            : []),
          {
            isSlitting: {
              $eq: true,
            },
          },
        ],
      },
      page: activePage,
      pageSize: 100,
      sort: "-id",
    }),
  );

  useEffect(() => {
    actions.setResetState();
  }, []);

  // 상태에 따라 rows 데이터를 업데이트 및 체크박스 초기화
  useEffect(() => {
    setWorkData(data?.rows);
    resetAllChecked();
  }, [data, workRowState]);

  useEffect(() => {
    const planWorks = data?.rows?.map((plan) => plan).flat() as WorksItemGet200ResponseRowsInner[];
    if (planWorks && planWorks.length > 0) {
      actions.setTempWorks((prevWorks) => [...prevWorks, ...planWorks]);
    }
  }, [data]);

  // OSPPONo, PODate, CustName, ItemName, ProcName 5가지 항목이 같은 경우 그룹핑
  const groupedData = useMemo(() => {
    const map = new Map();
    data?.rows?.forEach((row) => {
      const OSPPONo = (row.productionPlan?.additional as { syncKey: string })?.syncKey.split(
        "_",
      )[0];
      const PODate = row.lastWorkDate;
      const CustName = row.CustName;
      const itemName = row.itemName;
      const ProcName = row.routingData?.operation?.name;

      const key = `${OSPPONo}-${PODate}-${CustName}-${itemName}-${ProcName}`;

      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(row);
    });

    return Array.from(map.entries()).map(([key, orders]) => {
      const rows = orders[0]; // 같은 그룹이니까 첫 번째 꺼 기준으로

      return {
        key,
        itemCode: rows.itemCode,
        itemName: rows.itemName,
        scheduleSeq: rows.scheduleSeq,
        spec: rows.spec,
        equipmentCode: rows.equipmentCode,
        equipmentName: rows.equipmentName,
        orders,
      };
    });
  }, [data]);

  const itemsPerPage = 10;

  // 현재 페이지에 표시할 데이터 계산
  const paginatedGroupedData = useMemo(() => {
    const startIndex = (activePage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return groupedData.slice(startIndex, endIndex);
  }, [groupedData, activePage]);

  useEffect(() => {
    if (paginatedGroupedData.length < itemsPerPage && (data?.totalPages ?? 0) > activePage) {
      setPage((prev) => prev + 1);
    }
  }, [paginatedGroupedData, data, activePage]);

  return (
    <WorkExampleContainer>
      <LoadingOverlay />
      <Work.ActionBar>
        <Work.LeftActionBar>
          <Flex gap="sm" direction={"column"}>
            <Flex gap="sm">
              <EquipmentAutoComplete
                onChange={(value) => {
                  setEquipmentCode(value);
                }}
                placeholder={t("placeholder.select", { item: t("equipment.equipment") })}
              />
              <ItemAutoComplete
                onChange={(value) => {
                  setItemCode(value);
                }}
                placeholder={t("placeholder.select", { item: t("item.item") })}
                itemType="PRODUCT,SEMI_PRODUCT"
              />
              <Work.Button
                leftIcon={<IconCalendar />}
                onClick={() => {
                  openModal(
                    <Calendar dates={state.date} type="range" />,
                    null,
                    t("workButton.dateSelect"),
                    true,
                  ).then((value) => {
                    if (isCalendarDate(value)) {
                      actions.setDate(value);
                      actions.setWorks(
                        () =>
                          [] as (
                            | AuthSignupPost201ResponseEquipmentWorksInner
                            | ProductionPlansGet200ResponseRowsInnerWorksInner
                            | rowDataProps
                          )[],
                      );
                      setStartDate(value[0]);
                      setEndDate(value[1]);
                      resetAllChecked();
                    }
                  });
                }}
              >
                {t("workButton.dateSelect")}
              </Work.Button>
            </Flex>
          </Flex>
        </Work.LeftActionBar>
        <Work.RightActionBar>
          <Menu shadow="md" width={150} position="bottom-end">
            <Menu.Target>
              <Button color="gray" rightIcon={<IconSettings />}>
                Settings
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() => {
                  openModal(<DefectTypeSet />, null, t("workButton.defectSetting")).then(
                    (result) => {
                      // result 데이터가 특정 데이터일 경우 불량 타입 재조회 (불량 유형 설정을 완료했다는 의미)
                      if (Array.isArray(result)) {
                        setDefectTypes(result as DefectTypeItem[][]);
                      }
                    },
                  );
                }}
              >
                Settings
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Work.RightActionBar>
      </Work.ActionBar>
      <Work.WorkHeader dateType="range" startDate={startDate} endDate={endDate} />
      <Work.WorkTableActionBar>
        <Work.LeftActionBar>
          <WorkSlittingLeftActionButtonBar matchedData={matchedData} />
        </Work.LeftActionBar>
        <Work.RightActionBar>
          <WorkSlittingRightActionButtonBar
            matchedData={matchedData}
            refetch={refetch}
            setUpdateSuccess={setUpdateSuccess}
            setLoading={setLoading}
            partChecked={partChecked}
          />
        </Work.RightActionBar>
      </Work.WorkTableActionBar>
      <Work.WorkTableActionBar>
        <Work.LeftActionBar></Work.LeftActionBar>
        <Work.RightActionBar>
          <ChipsBox>
            {Object.keys(trackingStatusObj).map((key) => (
              <Chip
                variant="filled"
                size="md"
                radius="sm"
                key={key}
                checked={workRowState[key] || false}
                onClick={() => {
                  toggle(key);
                  actions.setWorks((prevWorks) => []);
                }}
              >
                {t(trackingStatusObj[key])}
              </Chip>
            ))}
          </ChipsBox>
        </Work.RightActionBar>
      </Work.WorkTableActionBar>
      <Work.WorkTableWrapper>
        <Work.WorkProductionPlanTable>
          <Work.WorkSlittingTableHead formReset={updateSuccess} />
          <tbody>
            {paginatedGroupedData && paginatedGroupedData.length ? (
              paginatedGroupedData.map((group, groupIdx) => {
                const {
                  itemCode,
                  itemName,
                  scheduleSeq,
                  equipmentCode,
                  equipmentName,
                  spec,
                  orders,
                } = group;

                return (
                  <Work.WorkSlittingTableRow
                    rowData={{
                      itemCode,
                      itemName,
                      scheduleSeq,
                      equipmentCode,
                      equipmentName,
                      spec,
                    }}
                    key={groupIdx}
                    defectTypes={defectTypes && defectTypes[1]}
                    formReset={updateSuccess}
                    workIds={orders.reduce((acc: Record<string, string>, order: any) => {
                      acc[order.id] = order.id;
                      return acc;
                    }, {})}
                    onSlittingDataLoaded={(data) => {
                      setMatchedData((prev) => ({
                        ...prev,
                        ...data.reduce(
                          (acc: any, item: any) => {
                            acc[item.workId] = item;
                            return acc;
                          },
                          {} as Record<number, WorksSlittingScrapDetailPost200ResponseInner>,
                        ),
                      }));
                    }}
                  >
                    {(slittingData) => {
                      return (
                        <tr>
                          <Td colSpan={9} style={{ padding: "0" }}>
                            <Work.WorkTable isWithBorder={false}>
                              <Work.WorkTableBody>
                                <Work.WorkSlittingTableInRowHead
                                  slittingData={slittingData}
                                  rowData={group}
                                  workIds={orders.reduce(
                                    (acc: Record<string, string>, order: any) => {
                                      acc[order.id] = order.id;
                                      return acc;
                                    },
                                    {},
                                  )}
                                  onCheckedPart={(checked: boolean) => {
                                    const newCheckedState: Record<number, boolean> = {};
                                    orders.forEach((order: any) => {
                                      newCheckedState[order.id] = checked;
                                    });
                                    setPartChecked((prev) => ({
                                      ...prev,
                                      ...newCheckedState,
                                    }));
                                  }}
                                />
                                {orders.map((work: any, index: number) => {
                                  const slitting = slittingData
                                    ? Object.values(slittingData).find(
                                        (slitting) => slitting.workId === work.id,
                                      )
                                    : undefined;
                                  return (
                                    <Work.WorkSlittingTableInRowBody
                                      key={work.id}
                                      rowData={work}
                                      rowIndex={groupIdx}
                                      formReset={updateSuccess}
                                      slittingData={slitting}
                                      partChecked={partChecked}
                                      workIds={orders.reduce(
                                        (acc: Record<string, string>, order: any) => {
                                          acc[order.id] = order.id;
                                          return acc;
                                        },
                                        {},
                                      )}
                                    />
                                  );
                                })}
                              </Work.WorkTableBody>
                            </Work.WorkTable>
                          </Td>
                        </tr>
                      );
                    }}
                  </Work.WorkSlittingTableRow>
                );
              })
            ) : (
              <OperationOutsourceNotFound />
            )}
          </tbody>
        </Work.WorkProductionPlanTable>
      </Work.WorkTableWrapper>
      <Pagination
        onChange={(page) => {
          resetAllChecked();
          setPage(page);
          actions.setWorks(
            () =>
              [] as (
                | AuthSignupPost201ResponseEquipmentWorksInner
                | ProductionPlansGet200ResponseRowsInnerWorksInner
                | rowDataProps
              )[],
          );
        }}
        value={activePage}
        total={Math.ceil(groupedData.length / itemsPerPage)}
        size="lg"
        radius="sm"
      />
    </WorkExampleContainer>
  );
};

const WorkExampleContainer = styled.div`
  display: flex;
  padding: 8px;
  flex-direction: column;
  align-items: center; // flex-start;
  gap: 8px;
`;

const Td = styled.td<{ minWidth?: number; width?: number; isNumber?: boolean }>`
  min-width: ${(props) => (props.minWidth ? `${props.minWidth}rem` : `auto`)};
  width: ${(props) => (props.width ? `${props.width}%` : `auto`)};
`;

const StatusChip = styled(Chip)`
  label {
    min-width: 2rem;
    min-height: 2rem;
    height: 2.4rem;
    font-size: 1rem;
    color: gray;
  }
`;
